/**
 * useEmployeeSidebarItems — nav config for the Employee shell (/my/*).
 *
 * Returns the ordered list of sidebar entries shown in `EmployeeSidebar`.
 * Each item declares its Hugeicons glyph, label, route slug (joined onto
 * the shell prefix in `AppSidebar`), and a visibility flag.
 *
 * Keep this hook as the single source of truth for the employee nav —
 * adding, removing, or reordering items here propagates everywhere the
 * sidebar is rendered. For role- or feature-gated items, compute
 * `isVisible` inside the hook using `useCheckUserRole` / `useFeatureEntitlements`
 * so consumers stay declarative.
 *
 * @returns Memoised array of `NavItemConfig` ready to pass to `AppSidebar`.
 *
 * @example
 * const items = useEmployeeSidebarItems();
 * <AppSidebar items={items} shell="employee" />
 */
import { useMemo } from "react";
import {
  DashboardSquare01Icon,
  CreditCardIcon,
  CashbackIcon,
  FolderLibraryIcon,
  Wallet01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { NavItemConfig } from "components/nav/AppSidebar";
import useShowReimbursements from "hooks/useShowReimbursements";

/**
 * Nav config for the Employee shell sidebar.
 *
 * Memoised so referential equality holds across renders — safe to pass
 * directly into memoised child components without re-rendering on every
 * parent update.
 *
 * @returns Ordered list of sidebar items for `/my/*` routes.
 */
export function useEmployeeSidebarItems(): NavItemConfig[] {
  const showReimbursements = useShowReimbursements();
  return useMemo<NavItemConfig[]>(
    () => [
      {
        IconComponent: DashboardSquare01Icon,
        name: "Overview",
        url: "dashboard",
        isVisible: true,
      },
      {
        IconComponent: CashbackIcon,
        name: "Transactions",
        url: "transactions",
        isVisible: true,
      },
      {
        IconComponent: Wallet01Icon,
        name: "Reimbursements",
        url: "reimbursements",
        isVisible: showReimbursements,
      },
      {
        IconComponent: CreditCardIcon,
        name: "My Cards",
        url: "cards",
        isVisible: true,
      },
      {
        IconComponent: FolderLibraryIcon,
        name: "Receipt Library",
        url: "receipt-library",
        isVisible: true,
      },
    ],
    [showReimbursements],
  );
}
